import React, { FC, useCallback, useState } from 'react';
import { Button } from 'reactstrap';
import { ConventionSharedModel } from '../../../../shared/convention';
import { useTranslation } from '../../../translations';
import { Fetcher, omit, useConvention, useFetcher } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';
import { ConfigTextBoxSetting } from './AbstractLiveSettings';

export const ContactSettings: FC = () => {
  const config = useConvention();

  const result = useFetcher(async () => {
    return await api.getConvention(config.id);
  }, [config.id]);

  if (!result.success) {
    return <Fetcher result={result} />;
  }

  return <ContactSettingsForm refresh={result.refresh} value={result.data.convention} />;
};

const ContactSettingsForm: FC<{
  readonly value: ConventionSharedModel;
  readonly refresh: () => void;
}> = ({ value, refresh }) => {
  const { ts } = useTranslation();
  const [contact, setContact] = useState(value.emailFromOverride);
  const [saving, setSaving] = useState(false);
  const onSave = useCallback(async () => {
    try {
      setSaving(true);
      await api.updateConvention(value.id, { emailFromOverride: contact });
      refresh();
    } catch (error) {
      captureError(error as Error);
    }

    setSaving(false);
  }, [value.id, contact, refresh]);

  const updateEmail = useCallback(
    (key: string, email: string) => {
      if (!email) {
        setContact((old) => omit(old, key));
      }

      setContact((old) => ({
        ...old,
        [key]: email,
      }));
    },
    [setContact],
  );

  return (
    <div>
      <ConfigTextBoxSetting
        description="contact_for_dealer_applications_this"
        displayName="dealers_email"
        name="dealers"
        placeholder="dealers"
        saving={saving}
        update={(val) => {
          updateEmail('dealers', val!);
        }}
        value={contact['dealers'] ?? ''}
      />
      <ConfigTextBoxSetting
        description="contact_point_for_volunteers_this"
        displayName="hr_email"
        name="hr"
        placeholder="hr"
        saving={saving}
        update={(val) => {
          updateEmail('hr', val!);
        }}
        value={contact['hr'] ?? ''}
      />
      <ConfigTextBoxSetting
        description="contact_point_for_registration_this"
        displayName="registration_email"
        name="registration"
        placeholder="registration"
        saving={saving}
        update={(val) => {
          updateEmail('registration', val!);
        }}
        value={contact['registration'] ?? ''}
      />

      <Button className="float-end" color="primary" disabled={saving} onClick={onSave}>
        {ts('common.save')}
      </Button>
    </div>
  );
};
